import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { removeCustomText, setFixedTextOverride, toggleFixedText, updateCustomText } from '../doc/textOps';
import { useMusicVideoEditor, type MusicVideoTextKey } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { formatTime } from '../timeline/timelineGeometry';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const TITLES = {
  title: panelMessages.titleItem,
  name: panelMessages.nameItem,
  endCard: panelMessages.endCardItem,
} as const;

/**
 * The inspector of a text (design d2): a fixed text has a switch and, for the title and the name,
 * its own words (empty = the song value); a custom text has its words, its start and end (set to
 * the play head) and Remove.
 */
const TextInspectorComponent: React.FC<{ textKey: MusicVideoTextKey }> = ({ textKey }) => {
  const intl = useIntl();
  const fieldId = useId();
  const { doc, data, change, select, transport, readOnly } = useMusicVideoEditor();
  if (textKey.startsWith('custom:')) {
    const id = textKey.slice('custom:'.length);
    const item = doc.texts.custom.find((text) => text.id === id);
    if (item === undefined) {
      return null;
    }
    return (
      <>
        <h4 className={styles.inspectorHead}>{item.text}</h4>
        <label className={styles.column} htmlFor={fieldId}>
          <span className={styles.eyebrow}>{intl.formatMessage(panelMessages.textField)}</span>
          <input
            id={fieldId}
            className={styles.textInput}
            value={item.text}
            disabled={readOnly}
            onChange={(event) =>
              change((current) => updateCustomText(current, id, { text: event.target.value }), `text:${id}`)
            }
          />
        </label>
        <div className={styles.inspectorGrid}>
          <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.starts)}</span>
          <button
            type="button"
            className={styles.field}
            disabled={readOnly}
            onClick={() =>
              change((current) => updateCustomText(current, id, { startSec: transport.livePositionSec() }))
            }
          >
            {formatTime(item.startSec, true)}
            <span className={styles.fieldUnit}>{intl.formatMessage(panelMessages.setToPlayHead)}</span>
          </button>
          <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.ends)}</span>
          <button
            type="button"
            className={styles.field}
            disabled={readOnly}
            onClick={() => change((current) => updateCustomText(current, id, { endSec: transport.livePositionSec() }))}
          >
            {formatTime(item.endSec, true)}
            <span className={styles.fieldUnit}>{intl.formatMessage(panelMessages.setToPlayHead)}</span>
          </button>
        </div>
        <button
          type="button"
          className={clsx(styles.ghostButton, styles.dangerButton)}
          disabled={readOnly}
          onClick={() => {
            change((current) => removeCustomText(current, id));
            select(null);
          }}
        >
          {intl.formatMessage(panelMessages.remove)}
        </button>
      </>
    );
  }
  const key = textKey as 'title' | 'name' | 'endCard';
  const isOn = key === 'endCard' ? doc.texts.endCard.on : doc.texts[key].on;
  return (
    <>
      <h4 className={styles.inspectorHead}>{intl.formatMessage(TITLES[key])}</h4>
      <div className={styles.row}>
        <button
          type="button"
          role="switch"
          aria-checked={isOn}
          aria-label={intl.formatMessage(panelMessages.show)}
          disabled={readOnly}
          className={clsx(styles.toggle, isOn && styles.toggleOn)}
          onClick={() => change((current) => toggleFixedText(current, key))}
        />
        <span>{intl.formatMessage(panelMessages.show)}</span>
      </div>
      {key !== 'endCard' && (
        <label className={styles.column} htmlFor={fieldId}>
          <span className={styles.eyebrow}>{intl.formatMessage(panelMessages.textField)}</span>
          <input
            id={fieldId}
            className={styles.textInput}
            value={doc.texts[key].text}
            placeholder={key === 'title' ? data.song.title : data.song.artistName}
            aria-label={intl.formatMessage(
              key === 'title' ? panelMessages.textFieldSongTitle : panelMessages.textFieldArtist
            )}
            disabled={readOnly}
            onChange={(event) =>
              change((current) => setFixedTextOverride(current, key, event.target.value), `text:${key}`)
            }
          />
        </label>
      )}
      <span className={styles.grow} />
      <p className={styles.hint}>{intl.formatMessage(panelMessages.textHint)}</p>
    </>
  );
};

export const TextInspector = TypedMemo(TextInspectorComponent);
